// SPDX-License-Identifier: AGPL-3.0-or-later

import {showGenericErrorModal} from '@app/features/app/components/alerts/GenericErrorModalCommands';
import {BLUESKY_PROVIDER_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import UserConnection from '@app/features/connection/state/UserConnection';
import {
	type CompletableConnectionType,
	forgetConnectionSignIn,
	isConnectionSignInPending,
	parseConnectionCallback,
	rememberConnectionSignIn,
} from '@app/features/connection/utils/ConnectionCallback';
import {CONNECTION_PROVIDER_NAMES} from '@app/features/connection/utils/ConnectionProviders';
import {SOMETHING_WENT_WRONG_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as FailureInspect from '@app/features/platform/utils/ResponseInspection';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {isDesktop, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import {handleAccountLimitedError} from '@app/features/user/utils/AccountLimitUtils';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {
	type ConnectionType,
	ConnectionTypes,
	type OAuthConnectionType,
} from '@fluxer/constants/src/ConnectionConstants';
import type {
	CompleteConnectionRequest,
	ConnectionAuthorizeResponse,
	ConnectionListResponse,
	ConnectionResponse,
	ConnectionVerificationResponse,
	CreateConnectionRequest,
	ReorderConnectionsRequest,
	UpdateConnectionRequest,
	VerifyAndCreateConnectionRequest,
} from '@fluxer/schema/src/domains/connection/ConnectionSchemas';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const FAILED_TO_INITIATE_CONNECTION_DESCRIPTOR = msg({
	message: "Couldn't start the connection",
	comment: 'Toast error shown when creating a new account connection fails.',
});
const FAILED_TO_START_AUTHORISATION_DESCRIPTOR = msg({
	message: "Couldn't start {blueskyProviderName} authorization",
	comment:
		'Toast error shown when starting the OAuth flow for a connection provider fails. Preserve {blueskyProviderName}; it is inserted by code and must appear verbatim in the translation.',
});
const CONNECTION_VERIFIED_DESCRIPTOR = msg({
	message: 'Connection verified',
	comment: 'Success toast after an account connection is verified.',
});
const FAILED_TO_VERIFY_CONNECTION_DESCRIPTOR = msg({
	message: "Couldn't verify the connection",
	comment: 'Toast error shown when verifying an account connection fails.',
});
const CONNECTION_UPDATED_DESCRIPTOR = msg({
	message: 'Connection updated',
	comment: 'Short label in the connection connection commands.',
});
const CONNECTION_REMOVED_DESCRIPTOR = msg({
	message: 'Connection removed',
	comment: 'Short label in the connection connection commands.',
});
const CONNECTIONS_REORDERED_DESCRIPTOR = msg({
	message: 'Connections reordered',
	comment: 'Short label in the connection connection commands.',
});
const FAILED_TO_START_PROVIDER_SIGN_IN_DESCRIPTOR = msg({
	message: "Couldn't start the {providerName} sign-in",
	comment:
		'Error shown when starting the sign-in for an account connection (Steam, Twitch, Kick, Battle.net, YouTube) fails. providerName is the provider brand name.',
});
const PROVIDER_CONNECTED_DESCRIPTOR = msg({
	message: '{providerName} account connected',
	comment:
		'Success toast after an account connection is completed in the desktop app. providerName is the provider brand name.',
});
const SIGN_IN_EXPIRED_DESCRIPTOR = msg({
	message: 'This sign-in link expired or was already used. Start again from Settings > Connections.',
	comment: 'Error when an account connection callback carries an unknown, expired or used sign-in state.',
});
const PROVIDER_DID_NOT_CONFIRM_DESCRIPTOR = msg({
	message: "{providerName} didn't confirm the account. Try again.",
	comment:
		'Error when the connection provider does not confirm the signed-in account. providerName is the provider brand name.',
});
const PROVIDER_DISABLED_DESCRIPTOR = msg({
	message: '{providerName} connections are turned off right now.',
	comment: 'Error when the instance has the connection provider switched off. providerName is the provider brand name.',
});
const CONNECTION_LIMIT_DESCRIPTOR = msg({
	message: 'You have reached the connection limit. Remove one to add another.',
	comment: 'Error when the account already has the maximum number of connections.',
});
const PROVIDER_CONNECTION_FAILED_DESCRIPTOR = msg({
	message: "Couldn't connect your {providerName} account. Try again.",
	comment: 'Fallback error when completing an account connection fails. providerName is the provider brand name.',
});
const logger = new Logger('Connections');

function connectionCreateRequest(type: ConnectionType, identifier: string): CreateConnectionRequest {
	return {
		type,
		identifier,
	};
}

function verifyAndCreateRequest(initiationToken: string, visibilityFlags?: number): VerifyAndCreateConnectionRequest {
	return {
		initiation_token: initiationToken,
		visibility_flags: visibilityFlags,
	};
}

function reorderConnectionsRequest(connectionIds: Array<string>): ReorderConnectionsRequest {
	return {
		connection_ids: connectionIds,
	};
}

function successToast(i18n: I18n, message: MessageDescriptor): void {
	ToastCommands.createToast({
		type: 'success',
		children: i18n._(message),
	});
}

function showErrorModal(i18n: I18n, error: unknown, fallbackMessage: MessageDescriptor): void {
	if (handleAccountLimitedError(error)) return;
	const errorMessage = FailureInspect.failureMessage(error);
	showGenericErrorModal({
		title: () => i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR),
		message: () => errorMessage ?? i18n._(fallbackMessage),
		dataFlx: 'connection.commands.connection-error-modal',
	});
}

export async function fetchConnections(): Promise<void> {
	try {
		const response = await http.get<ConnectionListResponse>(Endpoints.CONNECTIONS);
		UserConnection.setConnections(response.body);
		logger.debug('Successfully fetched connections');
	} catch (error) {
		logger.error('Failed to fetch connections:', error);
		throw error;
	}
}

export async function initiateConnection(
	i18n: I18n,
	type: ConnectionType,
	identifier: string,
): Promise<ConnectionVerificationResponse> {
	try {
		const response = await http.post<ConnectionVerificationResponse>(Endpoints.CONNECTIONS, {
			body: connectionCreateRequest(type, identifier),
		});
		logger.debug(`Successfully initiated connection: ${type}/${identifier}`);
		return response.body;
	} catch (error) {
		logger.error(`Failed to initiate connection ${type}/${identifier}:`, error);
		showErrorModal(i18n, error, FAILED_TO_INITIATE_CONNECTION_DESCRIPTOR);
		throw error;
	}
}

export async function authorizeBlueskyConnection(i18n: I18n, handle: string): Promise<void> {
	try {
		const response = await http.post<ConnectionAuthorizeResponse>(Endpoints.BLUESKY_AUTHORIZE, {body: {handle}});
		rememberConnectionSignIn(
			getProtectedLocalStorage(),
			ConnectionTypes.BLUESKY,
			response.body.authorize_url,
			Date.now(),
		);
		window.open(response.body.authorize_url, '_blank');
	} catch (error) {
		logger.error(`Failed to start Bluesky OAuth flow for ${handle}:`, error);
		showErrorModal(i18n, error, {
			...FAILED_TO_START_AUTHORISATION_DESCRIPTOR,
			values: {blueskyProviderName: BLUESKY_PROVIDER_NAME},
		});
		throw error;
	}
}

function openSignInWindow(): Window | null {
	if (isDesktop()) return null;
	try {
		return window.open('about:blank', '_blank');
	} catch {
		return null;
	}
}

async function sendToProvider(signInWindow: Window | null, authorizeUrl: string): Promise<void> {
	if (isDesktop()) {
		await openExternalUrl(authorizeUrl);
		return;
	}
	if (signInWindow && !signInWindow.closed) {
		signInWindow.opener = null;
		signInWindow.location.replace(authorizeUrl);
		return;
	}
	window.location.assign(authorizeUrl);
}

export async function authorizeConnection(i18n: I18n, type: OAuthConnectionType): Promise<void> {
	// Opened inside the click, before any await, so popup blockers let it through.
	const signInWindow = openSignInWindow();
	try {
		const response = await http.post<ConnectionAuthorizeResponse>(Endpoints.CONNECTION_AUTHORIZE(type), {body: {}});
		rememberConnectionSignIn(getProtectedLocalStorage(), type, response.body.authorize_url, Date.now());
		await sendToProvider(signInWindow, response.body.authorize_url);
	} catch (error) {
		signInWindow?.close();
		logger.error(`Failed to start the ${type} sign-in:`, error);
		showErrorModal(i18n, error, {
			...FAILED_TO_START_PROVIDER_SIGN_IN_DESCRIPTOR,
			values: {providerName: CONNECTION_PROVIDER_NAMES[type]},
		});
		throw error;
	}
}

export async function completeConnection(
	type: CompletableConnectionType,
	request: CompleteConnectionRequest,
): Promise<ConnectionResponse> {
	const response = await http.post<ConnectionResponse>(Endpoints.CONNECTION_COMPLETE(type), {body: request});
	UserConnection.addConnection(response.body);
	UserProfileCommands.clearCurrentUserProfiles();
	logger.debug(`Completed ${type} connection`);
	return response.body;
}

export function getCompleteErrorDescriptor(type: CompletableConnectionType, error: unknown): MessageDescriptor {
	const values = {providerName: CONNECTION_PROVIDER_NAMES[type]};
	switch (FailureInspect.failureCode(error)) {
		case APIErrorCodes.CONNECTION_INITIATION_TOKEN_INVALID:
			return SIGN_IN_EXPIRED_DESCRIPTOR;
		case APIErrorCodes.CONNECTION_VERIFICATION_FAILED:
			return {...PROVIDER_DID_NOT_CONFIRM_DESCRIPTOR, values};
		case APIErrorCodes.FEATURE_TEMPORARILY_DISABLED:
			return {...PROVIDER_DISABLED_DESCRIPTOR, values};
		case APIErrorCodes.CONNECTION_LIMIT_REACHED:
			return CONNECTION_LIMIT_DESCRIPTOR;
		default:
			return {...PROVIDER_CONNECTION_FAILED_DESCRIPTOR, values};
	}
}

export async function completeConnectionFromDeepLink(i18n: I18n, path: string): Promise<void> {
	const url = new URL(path, 'https://callback.invalid');
	const type = url.pathname.split('/')[2] ?? '';
	const result = parseConnectionCallback(type, url.search);
	if (result.kind !== 'complete') return;
	const completableType = type as CompletableConnectionType;
	const {state} = result.request;
	if (!isConnectionSignInPending(getProtectedLocalStorage(), type, state, Date.now())) {
		logger.warn(`Ignored a ${type} connection callback this app did not start`);
		return;
	}
	forgetConnectionSignIn(getProtectedLocalStorage(), type, state, Date.now());
	try {
		await completeConnection(completableType, result.request);
		successToast(i18n, {
			...PROVIDER_CONNECTED_DESCRIPTOR,
			values: {providerName: CONNECTION_PROVIDER_NAMES[completableType]},
		});
	} catch (error) {
		logger.error(`Failed to complete the ${type} connection:`, FailureInspect.failureCode(error));
		if (handleAccountLimitedError(error)) return;
		ToastCommands.createToast({type: 'error', children: i18n._(getCompleteErrorDescriptor(completableType, error))});
	}
}

export async function verifyAndCreateConnection(
	i18n: I18n,
	initiationToken: string,
	visibilityFlags?: number,
): Promise<ConnectionResponse> {
	try {
		const response = await http.post<ConnectionResponse>(Endpoints.CONNECTIONS_VERIFY_AND_CREATE, {
			body: verifyAndCreateRequest(initiationToken, visibilityFlags),
		});
		UserConnection.addConnection(response.body);
		UserProfileCommands.clearCurrentUserProfiles();
		successToast(i18n, CONNECTION_VERIFIED_DESCRIPTOR);
		logger.debug('Successfully verified and created connection');
		return response.body;
	} catch (error) {
		logger.error('Failed to verify and create connection:', error);
		showErrorModal(i18n, error, FAILED_TO_VERIFY_CONNECTION_DESCRIPTOR);
		throw error;
	}
}

export async function updateConnection(
	i18n: I18n,
	type: string,
	connectionId: string,
	patch: UpdateConnectionRequest,
): Promise<void> {
	try {
		await http.patch(Endpoints.CONNECTION(type, connectionId), {body: patch});
		UserConnection.updateConnection(connectionId, patch);
		UserProfileCommands.clearCurrentUserProfiles();
		successToast(i18n, CONNECTION_UPDATED_DESCRIPTOR);
		logger.debug(`Successfully updated connection: ${type}/${connectionId}`);
	} catch (error) {
		logger.error(`Failed to update connection ${type}/${connectionId}:`, error);
		throw error;
	}
}

export async function deleteConnection(i18n: I18n, type: string, connectionId: string): Promise<void> {
	try {
		await http.delete(Endpoints.CONNECTION(type, connectionId));
		UserConnection.removeConnection(connectionId);
		UserProfileCommands.clearCurrentUserProfiles();
		successToast(i18n, CONNECTION_REMOVED_DESCRIPTOR);
		logger.debug(`Successfully deleted connection: ${type}/${connectionId}`);
	} catch (error) {
		logger.error(`Failed to delete connection ${type}/${connectionId}:`, error);
		throw error;
	}
}

export async function verifyConnection(i18n: I18n, type: string, connectionId: string): Promise<void> {
	try {
		const response = await http.post<ConnectionResponse>(Endpoints.CONNECTION_VERIFY(type, connectionId), {
			body: {},
		});
		UserConnection.updateConnection(connectionId, response.body);
		UserProfileCommands.clearCurrentUserProfiles();
		successToast(i18n, CONNECTION_VERIFIED_DESCRIPTOR);
		logger.debug(`Successfully verified connection: ${type}/${connectionId}`);
	} catch (error) {
		logger.error(`Failed to verify connection ${type}/${connectionId}:`, error);
		throw error;
	}
}

export async function reorderConnections(i18n: I18n, connectionIds: Array<string>): Promise<void> {
	try {
		await http.patch(Endpoints.CONNECTIONS_REORDER, {body: reorderConnectionsRequest(connectionIds)});
		await fetchConnections();
		UserProfileCommands.clearCurrentUserProfiles();
		successToast(i18n, CONNECTIONS_REORDERED_DESCRIPTOR);
		logger.debug('Successfully reordered connections');
	} catch (error) {
		logger.error('Failed to reorder connections:', error);
		throw error;
	}
}
